import api from './api';

/**
 * PDFs, CSVs and settings images sit behind the bearer token, which a plain
 * <a href>/<img src> can't send — so every file is fetched as a blob.
 *
 * Errors are unwrapped before they are rethrown, so callers can hand them
 * straight to getApiError().
 */

/** With responseType 'blob', a 422/404 JSON body arrives as a Blob. Parse it back. */
export async function unwrapBlobError(error) {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob && data.type.includes('json')) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      /* leave the blob; getApiError falls back to error.message */
    }
  }
  return error;
}

const fetchBlob = async (url, params) => {
  try {
    return (await api.get(url, { params, responseType: 'blob' })).data;
  } catch (error) {
    throw await unwrapBlobError(error);
  }
};

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Print through a hidden same-origin iframe. Opening a tab after an await is
 * popup-blocked in most browsers, so that is only the fallback.
 */
const printBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const frame = Object.assign(document.createElement('iframe'), { src: url, title: 'Print invoice' });
  Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });

  const cleanup = () => {
    frame.remove();
    URL.revokeObjectURL(url);
  };

  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      window.open(url, '_blank');
    }
    // The print dialog blocks in most browsers; give it a generous grace period.
    setTimeout(cleanup, 60000);
  };
  document.body.appendChild(frame);
};

export const billingFiles = {
  /** Object URL for an <iframe>. The caller revokes it. */
  pdfUrl: async (invoiceId) => URL.createObjectURL(await fetchBlob(`/billing/invoices/${invoiceId}/pdf`)),

  downloadPdf: async (invoiceId, filename) =>
    saveBlob(await fetchBlob(`/billing/invoices/${invoiceId}/pdf`, { download: 1 }), filename || `invoice-${invoiceId}.pdf`),

  printPdf: async (invoiceId) => printBlob(await fetchBlob(`/billing/invoices/${invoiceId}/pdf`)),

  /** Proforma invoices: same as above, under /billing/proforma-invoices. */
  proformaPdfUrl: async (id) => URL.createObjectURL(await fetchBlob(`/billing/proforma-invoices/${id}/pdf`)),

  downloadProformaPdf: async (id, filename) =>
    saveBlob(await fetchBlob(`/billing/proforma-invoices/${id}/pdf`, { download: 1 }), filename || `proforma-invoice-${id}.pdf`),

  printProformaPdf: async (id) => printBlob(await fetchBlob(`/billing/proforma-invoices/${id}/pdf`)),

  downloadTemplate: async () =>
    saveBlob(await fetchBlob('/billing/imports/template'), 'billing-clients-template.csv'),

  downloadImportReport: async (batchId) =>
    saveBlob(await fetchBlob(`/billing/imports/${batchId}/errors`), `import-${batchId}-report.csv`),

  /** Object URL for an <img>. The caller revokes it. */
  imageUrl: async (key) => URL.createObjectURL(await fetchBlob(`/billing/settings/images/${key}`)),
};
